// ⌘K: find any record, go anywhere, do the page's actions. Pages add actions with usePaletteActions.
import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { api } from '../../../lib'
import { rememberList } from '../records/lib'
import { NAV_ITEMS } from './nav'

type Hit = { title: string; sub?: string; path: string }
type Result = { q: string; groups: { kind: string; label: string; rows: Hit[] }[]; top: string | null }
export type PaletteAction = { id: string; label: string; hint?: string; run: () => void }
type Row = { key: string; title: string; sub?: string; run: () => void; group: string }

const Ctx = createContext<{ open: () => void; setActions: (key: string, a: PaletteAction[]) => void } | null>(null)
export const usePalette = () => useContext(Ctx)!

/** A page's commands, shown first while it is open. */
export function usePaletteActions(key: string, actions: PaletteAction[]) {
  const p = usePalette()
  const labels = actions.map((a) => a.id).join('|')
  const ref = useRef(actions)
  ref.current = actions
  useEffect(() => {
    p.setActions(key, ref.current.map((a) => ({ ...a, run: () => ref.current.find((x) => x.id === a.id)?.run() })))
    return () => p.setActions(key, [])
  }, [key, labels, p])
}

/** Opening a record from here hands its neighbours to the record page, so Previous, Next and j k walk the same results. */
function keepList(rows: Hit[]) {
  for (const [kind, base] of [['company', '/staff/companies/'], ['person', '/staff/people/']] as const) {
    const ids = rows.filter((r) => r.path.startsWith(base)).map((r) => r.path.slice(base.length))
    if (ids.length) rememberList(kind, ids)
  }
}

function useDebounced(value: string, ms: number) {
  const [v, setV] = useState(value)
  useEffect(() => { const t = setTimeout(() => setV(value), ms); return () => clearTimeout(t) }, [value, ms])
  return v
}

export function PaletteProvider({ children }: { children: ReactNode }) {
  const [isOpen, setOpen] = useState(false)
  const [actions, set] = useState<Record<string, PaletteAction[]>>({})
  const value = useMemo(() => ({
    open: () => setOpen(true),
    setActions: (key: string, a: PaletteAction[]) => set((s) => ({ ...s, [key]: a })),
  }), [])
  useEffect(() => {
    const fn = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        // Over a confirm, the palette stays shut: one Esc must not close both.
        if (!document.querySelector('.st-dialog')) setOpen((o) => !o)
      }
      else if (e.key === '/' && !/^(INPUT|TEXTAREA|SELECT)$/.test((e.target as HTMLElement).tagName) && !document.querySelector('.st-scrim')) { e.preventDefault(); setOpen(true) }
    }
    document.addEventListener('keydown', fn)
    return () => document.removeEventListener('keydown', fn)
  }, [])
  return (
    <Ctx.Provider value={value}>
      {children}
      {isOpen ? <Box actions={Object.values(actions).flat()} close={() => setOpen(false)} /> : null}
    </Ctx.Provider>
  )
}

function Box({ actions, close }: { actions: PaletteAction[]; close: () => void }) {
  const [text, setText] = useState('')
  const [cur, setCur] = useState(0)
  const navigate = useNavigate()
  const typed = text.trim()
  const q = useDebounced(typed, 120)
  const [waiting, setWaiting] = useState(false)
  const search = useQuery({
    queryKey: ['staff', 'find', q],
    queryFn: () => api<Result>('GET', `/api/staff/find?q=${encodeURIComponent(q)}`),
    enabled: q.length >= 2,
    staleTime: 10_000,
    placeholderData: (p) => p,
  })
  const low = text.trim().toLowerCase()
  const go = (path: string) => () => { navigate(path); close() }
  const rows: Row[] = [
    ...actions.filter((a) => !low || a.label.toLowerCase().includes(low)).map((a) => ({ key: `do:${a.id}`, title: a.label, sub: a.hint, run: () => { a.run(); close() }, group: 'Do' })),
    ...(q.length >= 2 ? (search.data?.groups ?? []).flatMap((g) => g.rows.map((r) => ({ key: `${g.kind}:${r.path}:${r.title}`, title: r.title, sub: r.sub, run: () => { keepList(g.rows); go(r.path)() }, group: g.label }))) : []),
    ...NAV_ITEMS.filter((n) => !low || n.label.toLowerCase().includes(low)).map((n) => ({ key: `go:${n.to}`, title: n.label, sub: n.key ? `g ${n.key}` : undefined, run: go(n.to), group: 'Go' })),
  ]
  useEffect(() => { setCur(0) }, [text, search.data])
  const settled = typed === q && !search.isFetching
  useEffect(() => {
    if (waiting && settled) { setWaiting(false); rows[0]?.run() }
  }, [waiting, settled])   // Enter pressed early opens the top result for what was typed, never the last search's
  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') close()
    else if (e.key === 'ArrowDown') { e.preventDefault(); setCur((c) => Math.min(rows.length - 1, c + 1)) }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setCur((c) => Math.max(0, c - 1)) }
    else if (e.key === 'Enter') {
      e.preventDefault()
      if (typed.length >= 2 && (typed !== q || search.isFetching)) setWaiting(true)   // results for this text aren't in yet
      else rows[cur]?.run()
    }
  }
  let last = ''
  return (
    <div className="st-scrim" onMouseDown={(e) => { if (e.target === e.currentTarget) close() }}>
      <div className="st-pal" role="dialog" aria-modal="true" aria-label="Find anything" data-testid="st-palette">
        <input autoFocus placeholder="Find a company, person, page or id" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={onKey} data-testid="st-palette-input" aria-label="Find anything" />
        <div className="st-pal-list">
          {rows.map((r, i) => {
            const head = r.group !== last ? r.group : null
            last = r.group
            return (
              <div key={r.key}>
                {head ? <div className="st-pal-group">{head}</div> : null}
                <div className={`st-pal-item${i === cur ? ' cur' : ''}`} data-testid="st-palette-item" onMouseEnter={() => setCur(i)} onClick={r.run}>
                  <span>{r.title}</span><span>{r.sub}</span>
                </div>
              </div>
            )
          })}
          {!rows.length ? <div className="st-pal-group">{search.isFetching ? 'Looking' : 'Nothing found'}</div> : null}
        </div>
      </div>
    </div>
  )
}
